export const CHUCK_METRICS = [
     { group: "climate", code: "canopy.air_temperature", key: "airTemperature", unit: "°C", color: "#cf6d42", referenceCodes: ["setpoint.canopy.air_temperature.day", "setpoint.canopy.air_temperature.night"] },
     { group: "climate", code: "canopy.leaf_temperature", key: "leafTemperature", unit: "°C", color: "#bd4e45" },
     { group: "climate", code: "canopy.relative_humidity", key: "relativeHumidity", unit: "%", color: "#3b7f98", referenceCodes: ["setpoint.canopy.relative_humidity"] },
     { group: "climate", code: "ambient.co2", key: "co2", unit: "ppm", color: "#6b7d3b", referenceCodes: ["setpoint.ambient.co2"] },
     { group: "climate", code: "canopy.leaf_vpd", key: "vpd", unit: "kPa", color: "#8c5aa8", referenceCodes: ["setpoint.canopy.leaf_vpd"] },
     { group: "light", code: "light.ppfd", key: "ppfd", unit: "µmol/m²/s", color: "#d2a727", referenceCodes: ["setpoint.light.ppfd"] },
     { group: "light", code: "light.eppfd", key: "eppfd", unit: "µmol/m²/s", color: "#b97920" },
     { group: "light", code: "light.dli", key: "dli", unit: "mol/m²/d", color: "#8d8f2f" },
     { group: "light", code: "light.edli", key: "edli", unit: "mol/m²/d", color: "#74752a" },
     { group: "light", code: "light.illuminance", key: "illuminance", unit: "lx", color: "#c28b22" },
     { group: "substrate", code: "substrate.vwc", key: "substrateVwc", unit: "%", color: "#397f69" },
     { group: "substrate", code: "substrate.temperature", key: "substrateTemperature", unit: "°C", color: "#b66b42" },
     { group: "substrate", code: "substrate.bulk_ec", key: "bulkEc", unit: "mS/cm", color: "#4e6fa8" },
     { group: "substrate", code: "substrate.pore_water_ec", key: "poreWaterEc", unit: "mS/cm", color: "#6e5da8" },
     { group: "substrate", code: "substrate.dielectric_permittivity", key: "epsilon", unit: "εr", color: "#477d8c" },
     { group: "targets", code: "setpoint.ambient.co2", key: "co2Target", unit: "ppm", color: "#899660" },
     { group: "targets", code: "setpoint.canopy.leaf_vpd", key: "vpdTarget", unit: "kPa", color: "#a984ba" },
     { group: "targets", code: "setpoint.canopy.relative_humidity", key: "humidityTarget", unit: "%", color: "#6c9cad" },
     { group: "targets", code: "setpoint.canopy.air_temperature.day", key: "dayTemperatureTarget", unit: "°C", color: "#d69064" },
     { group: "targets", code: "setpoint.canopy.air_temperature.night", key: "nightTemperatureTarget", unit: "°C", color: "#7786a5" },
     { group: "targets", code: "setpoint.light.ppfd", key: "ppfdTarget", unit: "µmol/m²/s", color: "#d4ae55" },
]

// Setpoints remain separate telemetry series, but are rendered as references on
// their matching measured series instead of being presented as measurements.
export const CHUCK_METRIC_GROUPS = ["climate", "light", "substrate"]

export const CHUCK_OVERVIEW_METRIC_CODES = [
     "light.ppfd",
     "canopy.air_temperature",
     "canopy.leaf_temperature",
     "canopy.relative_humidity",
     "ambient.co2",
     "canopy.leaf_vpd",
     "light.illuminance",
]

export const CHUCK_SPECTRUM_WAVELENGTHS = [415, 445, 480, 515, 555, 590, 630, 680, 855]

const findSeries = (telemetry, code) => (telemetry?.series || []).find((item) => item.code === code)

export const chuckSpectrumCode = (prefix, wavelength) => (
     prefix === "light.spectrum.raw" && wavelength === 855 ? `${prefix}.nir` : `${prefix}.${wavelength}`
)

export const chuckMetricPoints = (telemetry, code) => {
     const series = findSeries(telemetry, code)
     const expectedIntervalSeconds = Number(series?.aggregation?.bucket_seconds) || 60
     let previousTimestamp = null
     return (series?.points || []).map((point, index) => {
          const sampledAt = point.received_at || point.observed_at
          const timestamp = new Date(sampledAt).getTime()
          const elapsedSeconds = previousTimestamp == null ? null : (timestamp - previousTimestamp) / 1000
          const missingBefore = Number.isFinite(elapsedSeconds) && elapsedSeconds > expectedIntervalSeconds * 1.5
               ? Math.max(1, Math.round(elapsedSeconds / expectedIntervalSeconds) - 1)
               : 0
          if (Number.isFinite(timestamp)) previousTimestamp = timestamp
          return {
               readingId: `${code}-${sampledAt || index}-${index}`,
               sampledAt,
               observedAt: point.observed_at,
               receivedAt: point.received_at,
               value: Number(point.value),
               minimum: point.minimum == null ? null : Number(point.minimum),
               maximum: point.maximum == null ? null : Number(point.maximum),
               sampleCount: point.sample_count == null ? 1 : Number(point.sample_count),
               missingBefore,
               expectedIntervalSeconds,
               qualityFlag: point.quality_flag,
               qualityFlags: point.quality_flags || (point.quality_flag ? [point.quality_flag] : []),
          }
     }).filter((point) => point.sampledAt && Number.isFinite(point.value))
}

export const latestChuckMetric = (telemetry, code) => {
     const points = chuckMetricPoints(telemetry, code)
     return points.length ? points[points.length - 1] : null
}

export const chuckSpectrum = (telemetry) => {
     const totalPpfd = latestChuckMetric(telemetry, "light.ppfd")
     const raw = CHUCK_SPECTRUM_WAVELENGTHS.map((wavelength) => latestChuckMetric(
          telemetry, chuckSpectrumCode("light.spectrum.raw", wavelength),
     ))
     const relative = CHUCK_SPECTRUM_WAVELENGTHS.map((wavelength) => latestChuckMetric(
          telemetry, chuckSpectrumCode("light.spectrum.relative", wavelength),
     ))
     const estimatedPpfd = CHUCK_SPECTRUM_WAVELENGTHS.map((wavelength) => latestChuckMetric(
          telemetry, chuckSpectrumCode("light.spectrum.estimated_ppfd", wavelength),
     ))
     const snapshotPoint = (point) => {
          if (!point) return null
          const qualityFlags = Array.from(new Set([
               ...(Array.isArray(point.qualityFlags) ? point.qualityFlags : []),
               point.qualityFlag,
          ].filter(Boolean).map((flag) => String(flag).trim().toLowerCase())))
          const excludedFromAnalysis = qualityFlags.includes("invalid")
          return {
               ...point,
               qualityFlags,
               value: excludedFromAnalysis ? null : point.value,
               excludedFromAnalysis,
          }
     }
     const rawPoints = raw.map(snapshotPoint)
     const relativePoints = relative.map(snapshotPoint)
     const estimatedPpfdPoints = estimatedPpfd.map(snapshotPoint)
     const totalPpfdPoint = snapshotPoint(totalPpfd)
     const rawValues = rawPoints.map((point) => point?.value ?? null)
     const relativeValues = relativePoints.map((point) => point?.value ?? null)
     const estimatedPpfdValues = estimatedPpfdPoints.map((point) => point?.value ?? null)
     const parRawTotal = rawValues.slice(0, -1).filter(Number.isFinite).reduce((sum, value) => sum + value, 0)
     const nirRaw = rawValues.at(-1)
     const completeRawSpectrum = rawValues.every(Number.isFinite)
     const timestamps = [...raw, ...relative, ...estimatedPpfd, totalPpfd].filter(Boolean).map((point) => point.receivedAt || point.sampledAt)
     return {
          wavelengths: CHUCK_SPECTRUM_WAVELENGTHS,
          raw: rawValues,
          relative: relativeValues,
          estimatedPpfd: estimatedPpfdValues,
          rawPoints,
          relativePoints,
          estimatedPpfdPoints,
          totalPpfdPoint,
          totalPpfd: totalPpfdPoint?.value ?? null,
          totalPpfdQualityFlag: totalPpfd?.qualityFlag ?? null,
          totalPpfdQualityFlags: totalPpfdPoint?.qualityFlags ?? [],
          nirRawShare: completeRawSpectrum && parRawTotal + nirRaw > 0 ? nirRaw / (parRawTotal + nirRaw) : null,
          receivedAt: timestamps.sort().at(-1) || null,
          available: raw.some(Boolean) || relative.some(Boolean) || estimatedPpfd.some(Boolean) || Boolean(totalPpfd),
     }
}

export const chuckLinkDiagnosis = (chuck, telemetry) => {
     if (!chuck?.feederNodePublicId || !chuck?.hardwareSerial) return "unassigned"
     if (!telemetry) return "source_unavailable"
     if (telemetry.node?.public_id !== chuck.feederNodePublicId) return "node_mismatch"
     if (String(telemetry.node?.serial_number || "").toUpperCase() !== String(chuck.hardwareSerial).toUpperCase()) {
          return "hardware_mismatch"
     }
     return Number(telemetry.status?.frame_count) > 0 ? "verified" : "linked_without_data"
}

export const chuckDisplayStatus = (chuck, telemetry) => {
     const diagnosis = chuckLinkDiagnosis(chuck, telemetry)
     if (diagnosis === "verified") return telemetry.status?.connection === "connected" ? "connected" : "stale"
     if ((chuck?.latestReading || chuck?.lastReceivedAt) && chuck?.connectionStatus === "connected") return "connected"
     if (diagnosis === "linked_without_data") return "linked_waiting"
     if (["node_mismatch", "hardware_mismatch"].includes(diagnosis)) return "mapping_error"
     if (diagnosis === "source_unavailable") return "source_unavailable"
     return chuck?.connectionStatus === "invalid_payload" ? "invalid_payload" : "unassigned"
}

export const chuckTelemetryWindow = (telemetry) => ({
     start: new Date(telemetry?.from || Date.now() - 3600000),
     end: new Date(telemetry?.to || Date.now()),
})
